import React, { useState, useEffect } from 'react';
import { getFeeHistory, getEarningsSummary } from '../services/bookingService';
import { formatPaise } from '../utils/money';

const DARK_RED = '#8B0000';
const AMBER_BG = '#FFF8E1';
const AMBER_TEXT = '#8D6E00';

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]}`;
};

/**
 * Poojari ka pending fee aur uska itihaas.
 *
 * ============================================================
 * PENDING FEE HAI KYA
 * ============================================================
 *
 * Platform fee har poori hui pooja pe banti hai. Par wo hamesha
 * katti nahi:
 *
 *   ONLINE booking -> paisa Razorpay se hoke aata hai, fee payout
 *                     se kat jaati hai. Hisaab barabar.
 *
 *   CASH booking   -> user ne poojari ko SEEDHA paisa diya. Fee
 *                     bani to hai par kati nahi - poora paisa
 *                     poojari ke haath gaya. Wo uska DENA ban
 *                     jaata hai.
 *
 * Wo dena agli ONLINE booking ke payout se apne aap kat jaata hai.
 *
 * ⚠️ POOJARI KO YE DIKHNA ZAROORI HAI. Bina iske, jab kabhi uska
 *    payout kam aayega to use lagega ki kuch galat kata hai - aur
 *    ek hi aisa shak poojari ko app se hata deta hai.
 *
 * ============================================================
 * ITIHAAS BOOKINGS SE BANTA HAI
 * ============================================================
 *
 * ⚠️ Koi alag ledger table nahi hai - jaan-boojh ke. Har booking
 *    ke saath paymentMode, platformFee, pendingFeeRecovered aur
 *    completedAt pehle se hain. Do tarah ki lines banti hain:
 *
 *      cash booking   -> + ₹50   (bakaya badha)
 *      online booking -> − ₹120  (bakaya kata)
 *
 *    Ek table kam, aur purani bookings ka itihaas bhi apne aap mil
 *    jaata hai.
 */
const FeeHistorySection = ({ poojariId }) => {

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingFee, setPendingFee] = useState(0);
    const [cashBlocked, setCashBlocked] = useState(false);

    useEffect(() => {
        if (!poojariId) return;

        let alive = true;
        setLoading(true);

                // ⚠️ Dono ek saath - Promise.all se. Ek ke baad ek karne se
        //    section do baar hilta hai (pehle history, phir pending
        //    fee), aur wo bura dikhta hai.
        Promise.all([
            getFeeHistory(poojariId),
            getEarningsSummary(poojariId)
        ])
            .then(([hist, earn]) => {
                if (!alive) return;
                setHistory(Array.isArray(hist) ? hist : []);
                setPendingFee(earn?.pendingFee || 0);
                setCashBlocked(earn?.cashBlocked || false);
            })
            .catch(() => {
                // ⚠️ Fail ho to khaali. Toast nahi dikha rahe - ye
                //    page ka mukhya kaam nahi hai, aur bekaar ka
                //    error poojari ko daraata hai.
                if (alive) {
                    setHistory([]);
                    setPendingFee(0);
                }
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => { alive = false; };
    }, [poojariId]);

    // ⚠️ Kuch bakaya nahi aur itihaas bhi khaali - to poora section
    //    hi mat dikhao. Naye poojari ko bekaar ka khaali box dikhana
    //    sirf uljhan badhata hai.
    if (!loading && (!pendingFee || pendingFee <= 0) && history.length === 0) {
        return null;
    }

    return (
        <div className="p-3 mb-3"
            style={{
                backgroundColor: 'white',
                border: '1px solid #EEE',
                borderRadius: '12px'
            }}>

            <p className="fw-bold mb-3"
                style={{ color: DARK_RED, fontSize: '15px' }}>
                Platform fee
            </p>

            {/* ⚠️ Bakaya sabse upar aur bada - yahi wo number hai
                jo poojari ko dekhna hai. */}
            {pendingFee > 0 && (
                <div className="p-3 mb-3"
                    style={{
                        backgroundColor: AMBER_BG,
                        borderRadius: '10px'
                    }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <span style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: AMBER_TEXT
                        }}>
                            Pending fee
                        </span>
                        <span style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: AMBER_TEXT
                        }}>
                            {formatPaise(pendingFee)}
                        </span>
                    </div>

                    {/* ⚠️ Cash band ho to wajah SAAF likhni hai.
                        Bina wajah ke poojari ko lagega app toota
                        hua hai, aur wo support ko phone karega. */}
                    <div style={{
                        fontSize: '12px',
                        color: AMBER_TEXT,
                        marginTop: '4px'
                    }}>
                        {cashBlocked
                            ? 'Cash bookings are paused until this is cleared. '
                              + 'It will be adjusted from your next online booking.'
                            : 'From cash poojas. Will be adjusted from your '
                              + 'next online booking.'}
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                    Loading...
                </p>
            ) : history.length === 0 ? (
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                    No fee activity yet.
                </p>
            ) : (
                <>
                    <p className="text-muted mb-2" style={{ fontSize: '12px' }}>
                        History
                    </p>

                    {history.map(item => (
                        <div key={item.bookingId}
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{
                                borderBottom: '1px solid #F5F5F5',
                                fontSize: '13px'
                            }}>
                            <div>
                                <div>{item.poojaType}</div>
                                <div className="text-muted"
                                    style={{ fontSize: '11px' }}>
                                    {formatDate(item.completedAt)}
                                    {' · '}
                                    {item.cash ? 'cash' : 'online'}
                                    </div>
                                    <div className="text-muted"
                                    style={{ fontSize: '11px' }}>
                                    {/* ⚠️ Poojari ko poora sandarbh chahiye.
                                        Sirf "+ ₹40" dekh ke wo sochta hai
                                        "kis cheez ka ₹40?" - paise ke maamle
                                        mein wo shak bura hota hai.

                                        ⚠️ CASH mein "pending" - fee kati
                                        nahi, uska dena hai.
                                        ONLINE mein "paid" - kat chuki. */}
                                    Booking {formatPaise(item.bookingAmount)}
                                    {' · Fee '}
                                    {formatPaise(Math.abs(item.amount))}
                                    {item.cash ? ' pending' : ' paid'}
                                    {' · Your earning '}
                                    {formatPaise(item.poojariPayout)}
                                </div>
                            </div>

                            {/* ⚠️ Rang SIGN se aata hai, cash/online se
                                nahi. Positive = bakaya badha (laal),
                                negative = kata (hara).

                                Poojari ke liye "kata" achhi khabar hai -
                                uska hisaab saaf ho raha hai. */}
                            <span style={{
                                fontWeight: 600,
                                color: item.amount > 0 ? '#C62828' : '#2E7D32'
                            }}>
                                {item.amount > 0 ? '+ ' : '− '}
                                {formatPaise(Math.abs(item.amount))}
                            </span>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default FeeHistorySection;